/* Indiehouse P1 — safe size confirmation. No mutation observers or polling.
   This layer never modifies Shopify's native radio selection or cart logic. */
(() => {
  if (window.__ihP1StableInstalled) return;
  window.__ihP1StableInstalled = true;

  const PICKER = 'variant-picker[context="main_product"][data-ih-p1-required="true"]';
  const GATE = 'buy-buttons[data-ih-p1-gated="true"]';
  const selectedByProduct = new Map();

  const rowFor = (picker) => picker?.querySelector('[data-ih-p1-size-row]');
  const radioFor = (picker) => rowFor(picker)?.querySelector('input[type="radio"]:checked');
  const scopeKey = (picker) => picker?.getAttribute('section-id') + ':' + picker?.getAttribute('handle');

  function pickerFor(gate) {
    const id = gate?.getAttribute('form');
    if (!id) return null;
    const section = gate.closest('.shopify-section--main-product') || document;
    return [...section.querySelectorAll(PICKER)].find(p => p.getAttribute('form-id') === id) || null;
  }

  function gateFor(picker) {
    if (!picker) return null;
    const section = picker.closest('.shopify-section--main-product') || document;
    return [...section.querySelectorAll(GATE)]
      .find(g => g.getAttribute('form') === picker.getAttribute('form-id')) || null;
  }

  function clearError(picker) {
    const row = rowFor(picker);
    if (!row) return;
    row.classList.remove('ih-p1-invalid');
    const error = row.querySelector('[data-ih-p1-error]');
    if (error) error.hidden = true;
  }

  function sync(picker) {
    const gate = gateFor(picker);
    if (!gate) return;
    const radio = radioFor(picker);
    const recorded = selectedByProduct.get(scopeKey(picker));
    const confirmed = !!(radio && !radio.disabled && recorded && recorded === radio.value);
    if (gate.dataset.ihP1Active !== String(confirmed)) {
      gate.dataset.ihP1Active = String(confirmed);
    }
    if (picker.dataset.ihP1Chosen !== String(confirmed)) {
      picker.dataset.ihP1Chosen = String(confirmed);
    }
    const label = rowFor(picker)?.querySelector('[data-ih-p1-value]');
    if (label) {
      const text = confirmed
        ? (radio.nextElementSibling?.textContent?.trim() || radio.value)
        : 'Select a size';
      if (label.textContent.trim() !== text) label.textContent = text;
    }
  }

  function confirm(picker, radio) {
    if (!picker || !radio || radio.disabled || !rowFor(picker)?.contains(radio)) return;
    selectedByProduct.set(scopeKey(picker), radio.value);
    clearError(picker);
    // A label click fires before the browser checks its radio. Reconcile on
    // the next animation frame; this runs ONCE, not in an observer loop.
    requestAnimationFrame(() => sync(picker));
  }

  function promptFor(picker) {
    if (!picker) return;
    const row = rowFor(picker);
    if (!row) return;
    row.classList.add('ih-p1-invalid');
    const error = row.querySelector('[data-ih-p1-error]');
    if (error) error.hidden = false;
    row.scrollIntoView({
      block: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
    const first = row.querySelector('input[type="radio"]:not([disabled]) + label');
    if (first && first.tabIndex >= 0) first.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!el) return;
    const prompt = el.closest('[data-ih-p1-action-prompt]');
    if (prompt) {
      event.preventDefault();
      event.stopImmediatePropagation();
      promptFor(pickerFor(prompt.closest(GATE)));
      return;
    }
    const label = el.closest('label[for]');
    const picker = label?.closest(PICKER) || el.closest(PICKER);
    if (!picker) return;
    const row = rowFor(picker);
    if (!row) return;
    let radio = null;
    if (label && row.contains(label)) {
      radio = document.getElementById(label.getAttribute('for'));
    } else if (el.matches('input[type="radio"]') && row.contains(el)) {
      radio = el;
    }
    if (radio?.matches('input[type="radio"]')) confirm(picker, radio);
  }, true);

  document.addEventListener('change', (event) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio') return;
    const picker = el.closest(PICKER);
    if (picker && rowFor(picker)?.contains(el)) confirm(picker, el);
  }, true);

  // Also protect Enter/keyboard submissions and sticky form-submit paths.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const gate = [...document.querySelectorAll(GATE)]
      .find(g => g.getAttribute('form') === form.id);
    if (!gate) return;
    const picker = pickerFor(gate);
    if (picker) sync(picker);
    if (gate.dataset.ihP1Active !== 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      promptFor(picker);
    }
  }, true);

  // Native variant updates may emit this event after replacing product markup.
  // A single frame reconciles the explicit choice if the new selected radio matches.
  document.addEventListener('variant:change', (event) => {
    requestAnimationFrame(() => {
      const section = event.target?.closest?.('.shopify-section--main-product') || document;
      section.querySelectorAll(PICKER).forEach(sync);
    });
  });

  function init() { document.querySelectorAll(PICKER).forEach(sync); }
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
